import { canCurrentUser } from '@utils/Permissions';
import { Capability } from '@utils/Permissions/Capabilities';
import {
    addDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    updateDoc,
    where,
    onSnapshot,
    Unsubscribe
} from 'firebase/firestore';
import {
    DeliveryType,
    isTableOrder,
    Order,
    ordersRef,
    OrderStatus,
    POINTS_LEDGER_ORDER_STATUSES
} from '@api/Orders/Order';
import {
    OrderPayment,
    PaymentMethod,
    buildCreatedWith,
    buildOrderPayments,
    getOrderPaidBy,
    getOrderPayments,
    toPaymentDay
} from '@api/Orders/OrderPayments';
import { PAYMENT_METHOD_LABELS, closingStatusChangeError } from '@api/Orders/OrderPaymentGate';
import store from '@store/index';
import UserServ from '@api/Users/UserServ';
import CustomerServ from '@api/Customers/CustomerServ';
import { ShoppingCartProduct } from '@api/ShoppingCart/ShoppingCart';
import { CashFlowType } from '@api/CashFlows/CashFlow';
import CashFlowServ from '@api/CashFlows/CashFlowServ';
import { differenceInSeconds } from 'date-fns';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeOrderChanges, getOrderStatusLabel } from '@api/Orders/OrderActivity';
import TwilioServ from '@api/Twilio/TwilioServ';
import RewardServ from '@api/Rewards/RewardServ';
import WalletServ from '@api/Wallet/WalletServ';
import { getEarnedPoints } from '@utils/Order';
import { toDateSafe } from '@utils/Date';
import TableKioskServ from '@api/TablesKiosks/TableKioskServ';
import InventoryServ from '@api/Inventory/InventoryServ';
import CustomerNotificationServ from '@api/CustomerNotifications/CustomerNotificationServ';
import { TableStatus } from '@api/TablesKiosks/TableKiosk';
import {
    bumpDrinksCounter,
    countOrderDrinks,
    getDrinkCategoryIds,
    unbumpDrinksCounter
} from '@api/Orders/DrinksCounter';
import { getPosCatalog } from '@api/Catalog/CatalogCache';

/**
 * Bebidas de un pedido según el catálogo vigente. Devuelve 0 ante cualquier
 * fallo: el bono no puede tirar el cierre ni la cancelación de una venta.
 */
const contarBebidasDelPedido = async (order: Order): Promise<number> => {
    try {
        const { productCategories } = await getPosCatalog();
        return countOrderDrinks(order, getDrinkCategoryIds(productCategories));
    } catch (error) {
        console.error('No se pudieron contar las bebidas del pedido', error);
        return 0;
    }
};

/** La fecha de la ORDEN: es la que decide a qué mes pertenece el bono. */
// La fecha de la ORDEN, no la de hoy: es la que decide a qué mes suma el bono
// de bebidas. El `new Date()` es el último recurso de un pedido sin fecha.
const fechaDelPedido = (order: Order): Date => toDateSafe(order?.date) ?? new Date();

/**
 * Pega el colaborador a cada pedido resolviendo **una vez por persona**, no una
 * vez por pedido.
 *
 * Esto corre dentro de un `onSnapshot`, o sea **en cada entrega**, y el tablero
 * vive abierto todo el día: una hidratación por documento ahí es la regla #3 del
 * CLAUDE.md raíz. `UserServ.getUser` ya cachea por uid (TTL 10 min, con dedupe),
 * así que el ahorro en lecturas es acotado — lo que se evita es el desfile de
 * ~180 `await` por entrega, y que una caché fría cobre 180 en vez de las ~5
 * personas que hay en un turno.
 */
const hydrateCollaborators = async (orders: Order[]): Promise<Order[]> => {
    const ids = Array.from(new Set(orders.map((order) => order.collaboratorId).filter(Boolean))) as string[];
    const porId = new Map<string, Awaited<ReturnType<typeof UserServ.getUser>>>();
    await Promise.all(
        ids.map(async (id) => {
            try {
                porId.set(id, await UserServ.getUser(id));
            } catch {
                // Un colaborador que ya no existe no puede dejar el tablero en blanco
            }
        })
    );
    orders.forEach((order) => {
        if (order.collaboratorId) {
            const user = porId.get(order.collaboratorId);
            if (user) order.collaboratorData = user;
        }
    });
    return orders;
};

export default {
    /**
     * Registra el canje de puntos de una orden en la cartera del cliente en cuanto
     * la orden queda pagada o en curso, para que el saldo baje de inmediato y el
     * canje aparezca en el historial sin esperar a que la orden se complete.
     * Es idempotente (CustomerServ verifica por referenceId), así que puede
     * llamarse desde varios puntos (crear, cobrar, completar, webhook) sin duplicar.
     */
    async recordPointsRedemption(order: Order): Promise<void> {
        if (!order.id || !order.customer?.uid || !((order.pointsAmount || 0) > 0)) {
            return;
        }
        if (!POINTS_LEDGER_ORDER_STATUSES.includes(order.status)) {
            return;
        }
        const reward = await RewardServ.getRewards();
        if (!reward) {
            return;
        }
        const pointsToRemove = ((order.pointsAmount || 0) / reward.reward.value) * reward.reward.costPoints;
        if (pointsToRemove > 0 && Number.isFinite(pointsToRemove)) {
            await CustomerServ.orderRemovePoints(order.customer.uid, order, pointsToRemove);
        }
    },
    async saveOrder(order: Order): Promise<Order> {
        if (!order.id) {
            const data = await addDoc(ordersRef, order);
            order.id = data.id;
            await updateDoc(doc(ordersRef, order.id), { ...order });
            await ActivityLogServ.addActivityLog('orders', order.id, 'creó este pedido');
            // Inventario (F5): el POS crea la orden ya comprometida (Paid/Preparing),
            // así que es el momento de descontar. NO bloquea la venta si falla — el
            // endpoint es idempotente y se puede reintentar (§4.2: "la venta nunca se
            // bloquea por inventario"). Las órdenes WaitingForPayment no pasan por
            // aquí con dinero encima: la web las compromete en el webhook de Stripe.
            //
            // ⚠️ VA PRIMERO, ANTES del ticket de WhatsApp (auditoría 2026-08-19). Iba
            // al final y el 35% de las ventas se quedaba SIN registro de consumo: si
            // `sendMessageTicket` fallaba —y es la primera llamada al server, la que
            // paga el arranque en frío de Fly— el throw se llevaba consigo el commit,
            // que ya nunca corría. Se verificó con `GET /inventory/orders/:id/preview`:
            // esas órdenes explotaban perfecto, simplemente nadie las mandó. Este
            // commit se traga sus propios errores, así que ponerlo primero no puede
            // romper la cadena; al revés sí la rompía.
            if (order.status !== OrderStatus.WaitingForPayment) {
                await InventoryServ.commitOrder(order.id, order.collaboratorId);
            }
            // El POS puede crear órdenes ya pagadas (cobro inmediato) o en preparación
            // (mesas): comprometer el canje de puntos desde este momento.
            await this.recordPointsRedemption(order);
            // Solo las MESAS cambian de estatus: un pedido de kiosco también trae
            // `tableKiosk`, pero un kiosco no se "ocupa" (usa kioskStatus, no tableStatus)
            if (isTableOrder(order)) {
                await TableKioskServ.updateTableStatus(order.tableKiosk?.id as string, TableStatus.ORDER_OPEN);
            }
            // El ticket de WhatsApp AL FINAL: es un efecto externo (Twilio vía el
            // server) y el único de esta lista que depende de un tercero. Si truena,
            // ya se guardó todo lo que importa.
            await this.sendMessageTicket(order.id);
            // ⚠️ **El POS NO avisa de sus propios pedidos** (decisión de Irving,
            // 2026-08-29). Quien está en la caja acaba de crear el pedido: un
            // aviso por cada venta llenaría la campana de ruido y la volvería
            // inútil justo para lo que sirve. El aviso de "pedido nuevo" es
            // SOLO para los de la web, y lo manda el webhook de Stripe cuando el
            // pago entra.
            return order;
        }
        await updateDoc(doc(ordersRef, order.id), { ...order });
        // Un pedido "Por pagar" que se retoma y se cobra en el POS pasa por AQUÍ, no
        // por el alta: nació `WaitingForPayment` y nunca descontó (14-oct: ~40 ventas
        // del 9 al 13-oct sin consumo). El server iguala el ledger a la venta; si ya
        // estaba igual no escribe nada.
        if (order.status !== OrderStatus.WaitingForPayment) {
            await InventoryServ.commitOrder(order.id, order.collaboratorId);
        }
        await ActivityLogServ.addActivityLog('orders', order.id, 'modificó este pedido');
        return order;
    },
    async getOrders(
        status?: OrderStatus,
        startDate?: Date,
        endDate?: Date,
        branchOfficeId?: string,
        salesChannelId?: string,
        deliveryType?: DeliveryType,
        includeCollaboratorData: boolean = true
    ): Promise<Order[]> {
        const orders: Order[] = [];
        const filters = [];
        if (status) {
            filters.push(where('status', '==', status));
        }
        if (startDate && endDate) {
            filters.push(where('date', '>=', startDate));
            filters.push(where('date', '<', endDate));
        }
        if (branchOfficeId) {
            filters.push(where('branchOfficeId', '==', branchOfficeId));
        }
        if (salesChannelId) {
            filters.push(where('salesChannel.id', '==', salesChannelId));
        }
        if (deliveryType) {
            filters.push(where('deliveryType', '==', deliveryType));
        }
        filters.push(orderBy('date', 'desc'));
        const queryDoc = query(ordersRef, ...filters);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return orders;
        }
        snapshot.docs.map((doc: any) => {
            orders.push(doc.data() as Order);
        });
        // Hydrating each order's collaborator is an extra Firestore read PER order (N+1),
        // which dominates the cost for large date ranges. Skip it when the caller doesn't
        // need collaborator data (e.g. counting product quantities).
        if (includeCollaboratorData) {
            const promise = orders.map(async (order, index) => {
                if (order.collaboratorId) {
                    orders[index].collaboratorData = await UserServ.getUser(order.collaboratorId);
                }
            });
            await Promise.all(promise);
        }
        return orders;
    },
    /**
     * La ventana de pedidos del tablero, en vivo.
     *
     * ⚠️ **No lo llames desde una pantalla directamente: usa
     * `subscribeOrdersWindow`** (`@api/Orders/OrdersWindowSubscription`), que
     * comparte la suscripción entre montajes. La PRIMERA entrega de un
     * `onSnapshot` trae la ventana completa —~180 pedidos en un día normal— así
     * que crear uno por montaje cobra esa ventana cada vez que alguien va del POS
     * al tablero y vuelve, que es el trabajo del día.
     *
     * Son DOS listeners porque un pedido entra a la ventana por `date` o por
     * `reservationDate` (una reserva hecha ayer para hoy), y Firestore no hace OR
     * entre campos distintos sin índice compuesto. Los que caen en los dos se
     * cobran dos veces: es el precio conocido de no tener índices compuestos, y
     * las reservas son pocas.
     */
    getOrdersRealtime(
        callback: (orders: Order[]) => void,
        startDate?: Date,
        endDate?: Date,
        branchOfficeId?: string
    ): () => void {
        let ordersByCreationDate: Order[] = [];
        let ordersByReservationDate: Order[] = [];

        const emitOrders = () => {
            const allOrders = [...ordersByCreationDate, ...ordersByReservationDate];
            const uniqueOrdersMap = new Map<string, Order>();
            allOrders.forEach((o) => {
                if (o.id) uniqueOrdersMap.set(o.id, o);
            });
            const uniqueOrders = Array.from(uniqueOrdersMap.values());
            callback(uniqueOrders);
        };
        const filters1 = [];
        if (startDate && endDate) {
            filters1.push(where('date', '>=', startDate));
            filters1.push(where('date', '<', endDate));
        }
        if (branchOfficeId) {
            filters1.push(where('branchOfficeId', '==', branchOfficeId));
        }
        filters1.push(orderBy('date', 'desc'));

        const query1 = query(ordersRef, ...filters1);
        const unsubscribe1 = onSnapshot(query1, async (snapshot) => {
            const tempOrders: Order[] = [];
            snapshot.docs.forEach((doc) => {
                tempOrders.push({ id: doc.id, ...doc.data() } as Order);
            });
            ordersByCreationDate = await hydrateCollaborators(tempOrders);
            emitOrders();
        });

        let unsubscribe2: Unsubscribe = () => {};
        if (startDate && endDate) {
            const filters2 = [];
            filters2.push(where('reservationDate', '>=', startDate));
            filters2.push(where('reservationDate', '<', endDate));
            if (branchOfficeId) {
                filters2.push(where('branchOfficeId', '==', branchOfficeId));
            }
            filters2.push(orderBy('reservationDate', 'desc'));

            const query2 = query(ordersRef, ...filters2);
            unsubscribe2 = onSnapshot(query2, async (snapshot) => {
                const tempOrders: Order[] = [];
                snapshot.docs.forEach((doc) => {
                    tempOrders.push({ id: doc.id, ...doc.data() } as Order);
                });
                ordersByReservationDate = await hydrateCollaborators(tempOrders);
                emitOrders();
            });
        }
        return () => {
            unsubscribe1();
            unsubscribe2();
        };
    },
    /**
     * "Pedido por cobrar" del POS: los que esperan pago y los COMPLETADOS sin un solo
     * cobro registrado (`payments` vacío — se cerraron con "Completar sin registrar el
     * cobro").
     *
     * La segunda consulta preguntaba por los 3 escalares en 0; desde el 2026-10-08
     * pregunta por `payments == []`, que es lo que sobrevive a su retiro (F6 del plan de
     * pagos). Ese día se completó `payments[]` en las 998 órdenes que no lo traían y se
     * verificó en las 11 sucursales que las dos consultas devuelven los MISMOS pedidos.
     * Las 12 de jun-2023 sin arreglo no salen a propósito: de ellas no se sabe cómo se
     * pagaron (ausente = "no se sabe"; vacío = "no se cobró").
     *
     * Solo igualdades (Firestore las cruza con sus índices de un campo, sin índice
     * compuesto); `total > 0` va en cliente porque con las igualdades sí lo exigiría.
     * Lecturas por apertura: los que esperan pago + los completados sin cobro de la
     * sucursal, cortesías de $0 incluidas (~20–260 al 2026-10-08; crecen con las
     * cortesías, ~10 al mes en la sucursal con más).
     */
    async getPendingPaymentOrders(branchOfficeId: string): Promise<Order[]> {
        const waiting = query(
            ordersRef,
            where('status', '==', OrderStatus.WaitingForPayment),
            where('branchOfficeId', '==', branchOfficeId)
        );
        const completedUnpaid = query(
            ordersRef,
            where('status', '==', OrderStatus.Completed),
            where('payments', '==', []),
            where('branchOfficeId', '==', branchOfficeId)
        );
        const [snapshot, snapshot2] = await Promise.all([getDocs(waiting), getDocs(completedUnpaid)]);
        return [
            ...snapshot.docs.map((d) => d.data() as Order),
            ...snapshot2.docs.map((d) => d.data() as Order).filter((order) => (order.total || 0) > 0)
        ];
    },
    async getOrdersByPromotionalCode(promotionalCodeId: string): Promise<Order[]> {
        const orders: Order[] = [];
        const queryDoc = query(ordersRef, where('promotionalCode.id', '==', promotionalCodeId));
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return orders;
        }
        snapshot.docs.map((doc: any) => {
            orders.push(doc.data() as Order);
        });
        return orders;
    },
    // Devuelve las órdenes del cliente que cuentan para el libro de puntos
    // (pagadas o en curso; ver POINTS_LEDGER_ORDER_STATUSES). Las órdenes
    // borradas o canceladas no aparecen, así el recálculo las descarta solo.
    async getOrdersByUserId(userId: string): Promise<Order[]> {
        const orders: Order[] = [];
        const filtersQuery = [];
        filtersQuery.push(where('customer.uid', '==', userId));
        filtersQuery.push(where('status', 'in', POINTS_LEDGER_ORDER_STATUSES));
        const snapshot = await getDocs(query(ordersRef, ...filtersQuery));
        if (snapshot.empty) {
            return orders;
        }
        snapshot.docs.map((doc: any) => {
            orders.push(doc.data() as Order);
        });
        return orders;
    },
    async getOrder(orderId: string): Promise<Order> {
        const documentData = await getDoc(doc(ordersRef, orderId));
        return documentData.data() as Order;
    },
    // Borrado CRUDO: solo el doc y sus logs. Lo usa la consolidación de mesa, donde
    // las órdenes originales se funden en una nueva que hereda sus efectos (caja,
    // mesa, puntos siguen vivos ahí). Para borrar de verdad usa `purgeOrder`.
    async deleteOrder(orderId: string): Promise<void> {
        await deleteDoc(doc(ordersRef, orderId));
        await ActivityLogServ.deleteActivityLogs('orders', orderId);
        return;
    },
    async deleteMultipleOrders(orderIds: string[]): Promise<void> {
        for (const id of orderIds) {
            await this.deleteOrder(id);
        }
        return;
    },
    /**
     * Borrado de LIMPIEZA (admin): borra la orden y DESHACE sus efectos, para que el
     * sistema quede como si nunca hubiera existido — pensado para depurar pedidos de
     * prueba. Además del doc y sus activity logs:
     *  - Caja: borra los cash-flows que generó al completarse (si no, el corte queda
     *    con un ingreso en efectivo sin pedido detrás).
     *  - Mesa: la libera SOLO si no quedan otras órdenes activas en ella.
     *  - Puntos: reconstruye la cartera del cliente. El rebuild lee las órdenes que
     *    existen HOY, así que al no estar ésta, su ganancia y su canje desaparecen
     *    (misma mecánica idempotente de [[krispis-points-ledger-design]]).
     *  - Inventario: devuelve al stock TODO lo que la venta descontó en el ledger (su
     *    primera venta y sus correcciones; en una cuenta de mesa, también lo de las
     *    originales). Idempotente. Se hace ANTES de borrar el doc:
     *    el reverso se apoya en el movimiento del ledger, no en la orden, pero así el
     *    orden de operaciones queda claro si algo falla a mitad.
     * Devuelve un resumen de lo que tocó, para poder informarlo en la UI.
     */
    async purgeOrder(
        orderId: string,
        options?: { skipPointsRebuild?: boolean }
    ): Promise<{ cashFlowsDeleted: number; tableReleased: boolean; pointsRebuilt: boolean; customerId?: string }> {
        // Borrar un pedido deshace puntos y caja: los dos admins, el de sucursal solo en
        // SUS sucursales (2026-09-30). El gate va aquí, no en los botones: hay tres
        // (detalle de la lista, detalle del Dashboard y borrado múltiple).
        // Leer ANTES de borrar: después ya no hay de dónde sacar mesa ni cliente
        // (y aquí se necesita la sucursal para el gate).
        const order = await this.getOrder(orderId);
        // Un pedido sin sucursal (histórico) solo lo borra quien tiene todas.
        if (!canCurrentUser(Capability.OrdersPurge, { branchOfficeId: order?.branchOfficeId || 'sin-sucursal' })) {
            throw new Error('Solo un admin de esta sucursal elimina sus pedidos');
        }
        // Mismo caso que en `cancelOrder`: si el pedido estaba completado, sus
        // bebidas ya contaban para el bono. Se descuenta ANTES de borrarlo,
        // porque después no hay de dónde leer las líneas ni la sucursal.
        if (order?.status === OrderStatus.Completed && order.branchOfficeId) {
            const bebidas = await contarBebidasDelPedido(order);
            await unbumpDrinksCounter(order.branchOfficeId, bebidas, fechaDelPedido(order));
        }
        // El inventario se devuelve antes del borrado: el reverso vive en el ledger
        // (no en la orden), pero dejarlo primero evita quedarnos sin rastro si algo
        // falla a mitad del purgado
        await InventoryServ.revertOrder(orderId);
        await this.deleteOrder(orderId);

        const cashFlowsDeleted = await CashFlowServ.deleteCashFlowsByOrderId(orderId);

        let tableReleased = false;
        if (isTableOrder(order)) {
            // Ojo: solo liberar si la mesa quedó sin pedidos activos (una mesa puede
            // tener varias órdenes abiertas y las demás siguen ocupándola).
            const tableId = order?.tableKiosk?.id as string;
            const remaining = await this.getOrdersByTable(tableId);
            if (remaining.length === 0) {
                await TableKioskServ.updateTableStatus(tableId, TableStatus.AVAILABLE);
                tableReleased = true;
            }
        }

        let pointsRebuilt = false;
        const customerId = order?.customer?.uid;
        if (customerId && !options?.skipPointsRebuild) {
            const customer = await CustomerServ.getCustomer(customerId);
            if (customer) {
                await CustomerServ.correctUsersPointsBasedOnOrders([customer]);
                pointsRebuilt = true;
            }
        }

        return { cashFlowsDeleted, tableReleased, pointsRebuilt, customerId };
    },
    // Purga en lote: un solo rebuild de puntos por cliente al final (borrar 10 órdenes
    // del mismo cliente no necesita 10 reconstrucciones de su cartera).
    async purgeMultipleOrders(orderIds: string[]): Promise<void> {
        const customerIds = new Set<string>();
        for (const id of orderIds) {
            const result = await this.purgeOrder(id, { skipPointsRebuild: true });
            if (result.customerId) {
                customerIds.add(result.customerId);
            }
        }
        for (const customerId of Array.from(customerIds)) {
            const customer = await CustomerServ.getCustomer(customerId);
            if (customer) {
                await CustomerServ.correctUsersPointsBasedOnOrders([customer]);
            }
        }
        return;
    },
    /**
     * Registra un cobro SOBRE un pedido que ya existe, **sumándolo** a lo que ya
     * tenía. No cambia el estado: el llamador decide qué hacer después.
     *
     * ## Por qué se AGREGA y no se reemplaza
     * `payOrder` (cobrar pendientes del POS) **sobrescribe** `payments[]`, porque
     * ahí el cajero captura el cobro completo de una sola vez. Aquí no: esto
     * existe para el pedido que se está cerrando y debe algo —una mesa
     * consolidada, un pedido cobrado a medias— así que un cobro nuevo **se suma**
     * al que ya estaba. Los 3 escalares se recalculan como la SUMA por método
     * sobre el arreglo resultante, que es lo que mantiene las dos
     * representaciones imposibles de desincronizar (regla del dual-write, F3).
     *
     * Lleva `cashRegisterId` y `collaboratorId`: es el dato que el modelo viejo
     * nunca tuvo y el que le falta al corte para atribuir un cobro tardío a la
     * caja correcta en vez de adivinarlo por ventana de tiempo.
     *
     * ⚠️ **No crea el cash-flow del efectivo**: lo hace `completeOrder`, que lee
     * el efectivo con `getOrderPaidBy` sobre el pedido ya guardado. Por eso esto
     * corre ANTES de completar — al revés, el cash-flow nacería sin el cobro.
     */
    async registerCollectedPayment(orderId: string, method: PaymentMethod, amount: number): Promise<void> {
        if (!(amount > 0)) return;
        const order = await this.getOrder(orderId);
        const previos = getOrderPayments(order);
        const pago: OrderPayment = {
            method,
            amount,
            date: new Date(),
            day: toPaymentDay(new Date())
        };
        const cashRegisterId = store.getState().krispis.cashRegister?.id;
        const collaboratorId = store.getState().auth.loggedUser?.uid;
        if (cashRegisterId) pago.cashRegisterId = cashRegisterId;
        if (collaboratorId) pago.collaboratorId = collaboratorId;
        await updateDoc(doc(ordersRef, orderId), {
            payments: [...previos, pago],
            lastUpdate: new Date()
        });
        await ActivityLogServ.addActivityLog(
            'orders',
            orderId,
            `registró el cobro de ${PAYMENT_METHOD_LABELS[method]} por $${amount.toFixed(2)}`
        );
    },
    /**
     * Deja constancia de que alguien cerró el pedido **sin decir cómo se cobró**.
     *
     * Es la mitad no-bloqueante de la decisión de Irving: omitir el registro se
     * puede, pero no es invisible. Cuando en agosto hubo que repararlo (47 mesas,
     * $18,246.60), lo que faltaba era exactamente esto — saber quién cerró qué sin
     * cobro y cuándo, para no tener que inferirlo del silencio.
     */
    async logCompletedWithoutPayment(orderId: string): Promise<void> {
        await ActivityLogServ.addActivityLog(
            'orders',
            orderId,
            'completó este pedido SIN registrar cómo se cobró'
        );
    },
    /**
     * Cobra un pedido de "Pedido por cobrar" (POS → Ingresos y cobros). **No toca
     * `subtotal` ni `total`**: hasta el 2026-10-08 escribía `subtotal: total`, y a
     * todo pedido con promoción, puntos o envío le borraba el desglose al cobrarlo
     * (riesgo 3 del plan de pagos).
     */
    async payOrder(
        orderId: string,
        cashAmount: number,
        creditCardAmount: number,
        bankTransferAmount: number
    ): Promise<void> {
        const order = await this.getOrder(orderId);
        const newStatus = order.status === OrderStatus.Completed ? OrderStatus.Completed : OrderStatus.Paid;
        await updateDoc(doc(ordersRef, orderId), {
            status: newStatus,
            // La fecha del pago es AHORA —no la del pedido— porque este flujo cobra
            // un pendiente, a veces días después.
            payments: buildOrderPayments({
                cashAmount,
                creditCardAmount,
                bankTransferAmount,
                date: new Date(),
                cashRegisterId: store.getState().krispis.cashRegister?.id,
                collaboratorId: store.getState().auth.loggedUser?.uid
            }),
            lastUpdate: new Date()
        });
        // Cobrar un "Por pagar" es cuando entra el dinero: el inventario se iguala
        // aquí (idempotente; si ya descontó y no cambió, el server no escribe nada).
        await InventoryServ.commitOrder(orderId, store.getState().auth.loggedUser?.uid);
        // La orden ya está pagada: comprometer el canje de puntos de inmediato
        // (idempotente; si ya se registró al crearla o completarla, no duplica).
        await this.recordPointsRedemption({ ...order, status: newStatus });
        await ActivityLogServ.addActivityLog('orders', orderId, 'realizó el pago de este pedido');
        return;
    },
    async completeOrder(orderId: string): Promise<void> {
        const order = await this.getOrder(orderId);
        // ¿Ya estaba completado? Entonces esta corrida es un reintento y el bono
        // de bebidas NO debe volver a sumar. El resto del método ya es
        // idempotente por su cuenta (puntos por `referenceId`, cash-flow por
        // orden); el contador es lo único que se duplicaría en silencio.
        const yaEstabaCompletado = order?.status === OrderStatus.Completed;
        // El efectivo se lee por `getOrderPaidBy`, no del escalar: así una orden
        // del modelo nuevo (`payments[]`) también genera su cash-flow.
        const cashPaid = getOrderPaidBy(order, PaymentMethod.Cash);
        if (cashPaid > 0) {
            // Quién, dónde y cuándo los pone `saveCashFlow` (la fecha, el instante real).
            const flow = {
                type: CashFlowType.Input,
                amount: cashPaid,
                orderId: order.id
            };
            await CashFlowServ.saveCashFlow(flow);
        }
        // Se declara fuera del `if` para poder decírselo al cliente en su aviso
        // de pedido completado, más abajo.
        let puntosGanados = 0;
        if (order.customer.uid) {
            const reward = await RewardServ.getRewards();
            if (reward) {
                // Ambas operaciones son idempotentes por referenceId: si el canje ya se
                // registró al pagar (webhook/POS) o completeOrder corre dos veces, no duplican.
                const pointsToRemove = ((order.pointsAmount || 0) / reward.reward.value) * reward.reward.costPoints;
                const pointsToAdd = getEarnedPoints(order, reward);
                puntosGanados = pointsToAdd;
                if (pointsToRemove > 0) {
                    await CustomerServ.orderRemovePoints(order.customer.uid, order, pointsToRemove);
                }
                if (pointsToAdd > 0) {
                    await CustomerServ.orderAddPoints(order.customer.uid, order, pointsToAdd);
                }
            }
            // El pase de Google Wallet espeja los puntos: aquí es donde el saldo
            // acaba de cambiar, así que aquí se avisa al teléfono del cliente.
            //
            // ⚠️ SIN await y con su propio catch, a propósito: es el MISMO
            // patrón que el commit de inventario. Completar una orden no puede
            // fallar porque Google no contestara, y el `throw` del ticket de
            // WhatsApp ya se llevó una vez un commit por delante (2026-08-19).
            // El server decide si notifica (solo si el saldo cambió) y tolera
            // que el cliente no tenga pase.
            void WalletServ.refreshPass(order.customer.uid);
        }
        const duration = differenceInSeconds(new Date(), order.date.toDate());
        await updateDoc(doc(ordersRef, orderId), {
            status: OrderStatus.Completed,
            lastUpdate: new Date(),
            duration
        });
        // Inventario: completar IGUALA el ledger a la venta (14-oct). Hasta ese día solo
        // descontaba el alta, y se escapaban un "Por pagar" que se cobró en el tablero,
        // lo que se agregó a una mesa ya descontada y la cuenta consolidada de una mesa
        // (sus originales descontaron, pero el consumo de la cuenta no quedaba en ningún
        // lado). El server no escribe nada si ya está igual, y en una cuenta de mesa solo
        // descuenta lo que no traían las originales (`consolidatedFrom[].orderId`).
        // Va DESPUÉS de escribir `Completed`: el server no descuenta un pedido que espera
        // pago, y una cuenta de mesa llega aquí todavía `WaitingForPayment`. Sin await y
        // nunca lanza: cocina no espera al server para completar (el server escala a cero).
        //
        // El descuento v1 que vivía aquí (`removeInputsFromInventory` por
        // `product.variables`) se fue el 2026-08-19 y no vuelve: escribía `stock` sin
        // `totalUnits` y se saltaba el ledger.
        void InventoryServ.commitOrder(orderId, store.getState().auth.loggedUser?.uid);
        await ActivityLogServ.addActivityLog('orders', orderId, 'completó este pedido');
        // Bono de bebidas: se SUMA a un contador por sucursal y mes en vez de que
        // el widget del sidebar cuente el mes entero en cada apertura del menú
        // (ver api/Orders/DrinksCounter.ts). Nunca lanza y el catálogo viene del
        // snapshot ya cacheado, así que no cuesta lecturas extra en el POS.
        if (!yaEstabaCompletado && order.branchOfficeId) {
            // La fecha es la de la ORDEN, no la de hoy: es la que usa el recuento.
            const bebidas = await contarBebidasDelPedido(order);
            await bumpDrinksCounter(order.branchOfficeId, bebidas, fechaDelPedido(order));
        }
        // "¡Gracias por tu compra!" — y desde aquí, con los puntos que acaba de
        // ganar, porque `getEarnedPoints` ya se calculó arriba.
        //
        // ⚠️ El pase de Google Wallet TAMBIÉN avisa de los puntos (D14). Hoy hay
        // 5 pases emitidos, así que el traslape es despreciable; cuando crezcan,
        // hay que decidir qué canal es dueño del aviso de puntos.
        await CustomerNotificationServ.notifyOrderStatus(order, OrderStatus.Completed, {
            esMesa: isTableOrder(order),
            puntosGanados: puntosGanados
        });
        if (isTableOrder(order)) {
            await TableKioskServ.updateTableStatus(order.tableKiosk?.id as string, TableStatus.AVAILABLE);
        }
        // Reseña de Google por WhatsApp: SOLO pedidos de la app a domicilio
        // (decisión del negocio, 2026-09-30; la regla la aplica el server en
        // `ReviewRule`). Va aquí para que salga igual la cierre quien la cierre —
        // hasta hoy dependía de la pantalla. El filtro local solo evita llamar al
        // server en cada venta de mostrador; un reintento no la repite.
        if (!yaEstabaCompletado && order.deliveryType === DeliveryType.Delivery) {
            await TwilioServ.sendOrderCompletedReview(orderId);
        }
        return;
    },
    async cancelOrder(orderId: string): Promise<void> {
        // Se lee ANTES de escribir: si el pedido ya estaba completado, sus
        // bebidas YA se sumaron al bono del colaborador y hay que devolverlas.
        // Sin esto, cancelar una venta ya cerrada dejaba el bono inflado y solo
        // se corregía re-sembrando el mes.
        //
        // La idempotencia sale gratis: el descuento depende de que el estado
        // ANTERIOR fuera `Completed`, y el `updateDoc` de abajo lo deja en
        // `Cancelled`, así que una segunda cancelación ya no descuenta.
        const order = await this.getOrder(orderId);
        const estabaCompletado = order?.status === OrderStatus.Completed;
        await updateDoc(doc(ordersRef, orderId), {
            status: OrderStatus.Cancelled,
            lastUpdate: new Date()
        });
        if (estabaCompletado && order.branchOfficeId) {
            const bebidas = await contarBebidasDelPedido(order);
            await unbumpDrinksCounter(order.branchOfficeId, bebidas, fechaDelPedido(order));
        }
        await ActivityLogServ.addActivityLog('orders', orderId, 'canceló este pedido');
        await CustomerNotificationServ.notifyOrderStatusById(orderId, OrderStatus.Cancelled);
        // Inventario: vuelve al stock TODO lo que la venta descontó en el ledger (la
        // primera venta y sus correcciones), nunca recalculado contra el catálogo de
        // hoy. Idempotente.
        await InventoryServ.revertOrder(orderId);
        return;
    },
    /**
     * Escritura PLANA del pedido, sin efectos secundarios (no dispara puntos,
     * inventario, WhatsApp ni cash-flow). La usan la edición manual de
     * `ModalViewOrder` y el "regresar un paso" del tablero. **No mete ni saca a
     * un pedido de `Completed`/`Cancelled`** (`closingStatusChangeError`): lanza.
     *
     * ⚠️ **Pásale `previous` siempre que lo tengas.** Con el pedido anterior,
     * la bitácora registra QUÉ cambió — *"cambió el total de $250.00 a
     * $300.00"*— en vez del inútil *"modificó este pedido"*. Hasta el
     * 2026-09-10 esto **no registraba nada**: se podía cambiar el total de una
     * venta, o moverla de día (y por tanto de corte de caja), sin dejar rastro.
     *
     * Si no cambió nada, **no se escribe log**: guardar sin tocar no es un evento.
     */
    async updateOrder(order: Order, previous?: Order | null): Promise<void> {
        // 🚨 Completar y cancelar tienen sus funciones (`completeOrder`, `cancelOrder`):
        // por aquí se perdían los puntos en silencio (el quinto camino, 2026-09-29).
        const cierre = closingStatusChangeError(order, previous);
        if (cierre) throw new Error(cierre);
        await updateDoc(doc(ordersRef, order.id), { ...order });
        if (order.id) {
            const description = previous
                ? describeOrderChanges(previous, order)
                : 'modificó este pedido (sin detalle del cambio)';
            if (description) {
                await ActivityLogServ.addActivityLog('orders', order.id, description);
            }
        }
        return;
    },
    async updateStatus(orderId: string, status: OrderStatus): Promise<void> {
        // ⚠️ **`Completed` NO se escribe aquí**: lo escribe `completeOrder`, que
        // además stampa `duration` en la MISMA escritura (2026-09-09).
        //
        // Escribirlo antes tenía dos consecuencias, y la segunda costaba dinero:
        // ① si `completeOrder` fallaba, el pedido quedaba marcado como completado
        //    sin haber movido caja, puntos ni inventario, y ya no se podía
        //    reintentar desde el tablero; y
        // ② `completeOrder` re-lee el pedido para decidir si ya estaba completado
        //    —el guard del contador del bono de bebidas—, así que por esta vía
        //    **siempre** lo veía completado y **nunca sumaba**. Se perdían las
        //    bebidas de todo pedido web (pickup y delivery), que son justo los
        //    que se completan por aquí.
        if (status !== OrderStatus.Completed) {
            await updateDoc(doc(ordersRef, orderId), { status, lastUpdate: new Date() });
        }
        await ActivityLogServ.addActivityLog(
            'orders',
            orderId,
            `actualizó el pedido a ${getOrderStatusLabel(status)}`
        );
        // Aviso al CLIENTE en su campana.
        //
        // Va aquí porque `updateStatus` es el paso obligado de TODAS las
        // acciones del tablero de cocina (marcar pagado, empezar, listo,
        // completar). `Completed` se salta a propósito: lo emite `completeOrder`,
        // que sí conoce los puntos ganados y lo llama este mismo switch de abajo.
        //
        // Es idempotente por `{orderId}_{status}` y **nunca lanza**: nada de la
        // operación puede romperse por un aviso. `stepBack` del tablero NO pasa
        // por aquí (usa `updateOrder`), así que retroceder un paso tampoco vuelve
        // a avisar.
        if (status !== OrderStatus.Completed) {
            await CustomerNotificationServ.notifyOrderStatusById(orderId, status);
        }
        switch (status) {
            case OrderStatus.Paid:
                await TwilioServ.sendOrderPaid(orderId);
                await TwilioServ.sendOrderPaidAdmin(orderId);
                return;
            case OrderStatus.Route:
                await TwilioServ.sendOrderRoute(orderId);
                return;
            case OrderStatus.Completed:
                // La reseña la decide `completeOrder` (solo delivery de la app).
                await this.completeOrder(orderId);
                return;
        }
        return;
    },
    // "En ruta" desde la lista de pedidos para el delivery que NO va con Uber
    // Direct (otra plataforma o repartidor local). **El link de seguimiento es
    // OPCIONAL** —con repartidor propio no existe— y cuando viene se persiste
    // AQUÍ, en la misma acción. Antes el link solo vivía en el estado local de
    // la pantalla: el botón lo exigía y nunca lo escribía en Firestore, así que
    // el cliente jamás lo veía en `/order-details` y el listener del tablero lo
    // borraba antes de dar clic (reporte de la operación, 2026-09-07). Vaciar el
    // campo NO borra un link ya guardado. El WhatsApp "en camino" y la campana
    // salen de `updateStatus` y no llevan el link.
    async routeOrder(orderId: string, routeUrl?: string): Promise<void> {
        if (routeUrl) {
            await updateDoc(doc(ordersRef, orderId), { routeUrl });
        }
        await this.updateStatus(orderId, OrderStatus.Route);
    },
    // El ticket YA NO viaja por WhatsApp: se consulta en la web.
    //
    // Antes este mensaje llevaba el pedido entero —productos, subtotal, envío,
    // promoción, puntos y total— dentro del propio WhatsApp, que es como se
    // hacía cuando no existía `app.krispis.mx`. Hoy manda la MISMA plantilla que
    // ya usaba la web al pagar (`order_paid`): lo mínimo (nombre, sucursal,
    // total) y un botón "Detalles del pedido" que abre
    // `/order-details?orderId=…`, donde el ticket está completo y siempre al
    // día. Un solo formato de aviso para mostrador, web, kiosco y mesa.
    //
    // Se reusa una plantilla YA APROBADA a propósito: en Twilio una plantilla
    // aprobada no se edita —se crea otra y vuelve a pasar por Meta—, así que
    // ésta es la forma de estandarizar sin esperar el trámite.
    //
    // **Interruptor**: el server decide si el aviso sale según Notificaciones →
    // WhatsApp (`message-flows`: general y "ventas del POS"). `manual = true` es
    // el botón "reenviar" de la lista: salta el apagado del POS —si alguien
    // apretó el botón, el cliente lo pidió—, no el general. Devuelve `skipped`
    // para que la pantalla lo diga en vez de fingir que se mandó.
    //
    // La bitácora del pedido solo registra el reenvío A MANO: el envío
    // automático de cada venta ya queda en `message-log` (D6: no duplicar lo
    // que deja rastro en otro lado), y el renglón "reenvió el aviso" en una
    // venta recién creada era falso.
    async sendMessageTicket(orderId: string, manual: boolean = false): Promise<{ skipped?: string }> {
        const order = await this.getOrder(orderId);
        // Venta de mostrador sin cliente identificado: no hay a quién mandarle.
        if (!order.customer?.phone) return {};
        const response = await TwilioServ.sendOrderPaid(orderId, manual);
        const skipped = response?.data?.skipped as string | undefined;
        if (skipped) return { skipped };
        if (manual) {
            await ActivityLogServ.addActivityLog('orders', orderId, 'reenvió el aviso de este pedido por WhatsApp');
        }
        return {};
    },
    // ⚠️ **El WhatsApp de "pedido listo" YA NO EXISTE** (2026-08-29, decisión de
    // Irving): por WhatsApp sale UN solo aviso por pedido —el de compra, con su
    // botón al detalle— y el resto pasa a las notificaciones internas, que no
    // cuestan por mensaje. Los pedidos de DELIVERY son la excepción y siguen
    // avisando por su camino propio (`sendOrderRoute`, "en camino").

    // Tables

    async getOrdersByTable(tableKioskId: string): Promise<Order[]> {
        const q = query(
            ordersRef,
            where('tableKiosk.id', '==', tableKioskId),
            where('status', 'not-in', [OrderStatus.Completed, OrderStatus.Cancelled])
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map((doc) => doc.data() as Order);
    },
    // Cierra la cuenta de una mesa consolidando sus órdenes activas en UNA sola
    // `WaitingForPayment` que el POS cobra. Con `customerUid` cierra SOLO lo de ese
    // cliente — necesario desde que una mesa puede tener consumos de personas
    // distintas (alguien que se fue sin pagar y el siguiente que se sentó ahí).
    //
    // La mesa NO se libera aquí: se libera cuando la cuenta se COMPLETA en el POS
    // (`completeOrder`). Liberarla antes dejaba la mesa "disponible" con una cuenta
    // sin pagar, y quien escaneara el QR entraba a esa cuenta.
    async completeTableOrders(tableKioskId: string, customerUid?: string): Promise<Order> {
        try {
            const orders = await this.getOrdersByTable(tableKioskId);

            if (orders.length === 0) {
                throw new Error('No hay órdenes activas para esta mesa');
            }

            // Candado anti doble consolidación: el botón vive en el tablero Y en la
            // pantalla de Mesas, así que dos personas pueden cerrar a la vez. Si ya
            // hay una cuenta consolidada esperando pago, se reusa en vez de duplicarla.
            const existente = orders.find(
                (order) => order.status === OrderStatus.WaitingForPayment && !!order.consolidatedFrom?.length
            );
            if (existente) {
                return existente;
            }

            const objetivo = customerUid ? orders.filter((order) => order.customer?.uid === customerUid) : orders;

            if (objetivo.length === 0) {
                throw new Error('No hay órdenes activas de ese cliente en la mesa');
            }

            const consolidatedOrder = await this.consolidateTableOrders(objetivo);

            const orderIds = objetivo.filter((order) => order.id).map((order) => order.id as string);

            if (orderIds.length > 0) {
                await this.deleteMultipleOrders(orderIds);
            }

            return consolidatedOrder;
        } catch (error) {
            console.error('Error completing table order:', error);
            throw error;
        }
    },
    async consolidateTableOrders(orders: Order[]): Promise<Order> {
        const baseOrder = orders[0];

        const allProducts: ShoppingCartProduct[] = [];
        let totalSubtotal = 0;

        const orderWithRegisteredUser = orders.find((order) => order.customer && order.customer.uid);

        const finalCustomer = orderWithRegisteredUser ? orderWithRegisteredUser.customer : baseOrder.customer;

        orders.forEach((order) => {
            allProducts.push(...order.products);
            totalSubtotal += order.subtotal;
        });

        const consolidatedOrder: Order = {
            shortId: baseOrder.shortId,
            status: OrderStatus.WaitingForPayment,
            products: allProducts,
            customer: finalCustomer,
            branchOfficeId: baseOrder.branchOfficeId,
            collaboratorId: baseOrder.collaboratorId,
            collaboratorData: baseOrder.collaboratorData,
            promotionalCode: baseOrder.promotionalCode,
            salesChannel: baseOrder.salesChannel,
            note: `Orden completada de: '${baseOrder.tableKiosk?.name}'`,
            // Trazabilidad: las órdenes originales se BORRAN (si quedaran, la venta
            // contaría doble), así que la cuenta guarda de qué pedidos salió: folio,
            // hora y total reales de cada uno
            consolidatedFrom: orders.map((order) => ({
                // Con el id, el server sabe qué descontaron ya las originales y solo
                // descuenta lo que la cuenta trae de más (14-oct)
                ...(order.id ? { orderId: order.id } : {}),
                shortId: order.shortId,
                date: order.date,
                total: order.total || order.subtotal || 0,
                customerName: `${order.customer?.profile?.name || ''} ${order.customer?.profile?.lastName || ''}`.trim()
            })),
            subtotal: totalSubtotal,
            promotion: 0,
            shippingCost: 0,
            total: totalSubtotal,
            pointsAmount: 0,
            // La propina que el cliente dejó desde su teléfono al pedir la cuenta
            // (F4 del PLAN-SUPERFICIES-CLIENTE-Y-PROPINA) viaja en las órdenes que
            // se consolidan: sin esto la cuenta consolidada nacería en 0 y el
            // cliente habría elegido propina para nada.
            tipAmount: orders.reduce((sum, order) => sum + (order.tipAmount || 0), 0),
            // La cuenta consolidada nace SIN cobrar: el arreglo vacío es la verdad
            // (y es distinto de "no se sabe" — eso sería no traer el campo).
            payments: [],
            createdWith: buildCreatedWith('admin', 'table-consolidation', process.env.REACT_APP_VERSION),
            date: baseOrder.date || new Date(),
            lastUpdate: new Date(),
            duration: 0,
            tableKiosk: baseOrder.tableKiosk
        };

        return await this.saveOrder(consolidatedOrder);
    }
};
